import * as React from 'react'
import { DatePicker, ConfigProvider } from 'antd'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { makeStyles } from '@material-ui/core/styles'
import { FormControl, InputLabel } from '@material-ui/core'
import { useState } from 'react'
import { convertDateFnsToDayjsFormat } from '../../utils/dates'
import dayjs from 'dayjs'
import localeData from 'dayjs/plugin/localeData'
import weekday from 'dayjs/plugin/weekday'
import '../../antd-custom.css'

dayjs.extend(customParseFormat)
dayjs.extend(localeData)
dayjs.extend(weekday)

export interface IDateTimePickerProps {
  id: string
  name?: string
  value: Date | null
  setValue?: (value: Date | null) => void
  style?: React.CSSProperties
  className?: string
  displayFormat: string // date-fns format
  size?: 'small' | 'middle' | 'large'
  label?: string
  onChange?: (dateTime: Date | null) => void
  required?: boolean
  showTime?: boolean
  use12Hours?: boolean
  disabled?: boolean
  width?: string | number
  shrinkLabel?: boolean
  inputReadOnly?: boolean
}

const useStyles = makeStyles({
  customPickerLabel: {
    position: 'absolute',
    left: 12,
    transformOrigin: 'top left',
    transition: 'all 0.2s ease-out',
    padding: '0 4px',
    zIndex: 1,
    pointerEvents: 'none'
  }
})

function DateTimePicker(props: IDateTimePickerProps) {
  const classes = useStyles()
  const [focused, setFocused] = useState(props.shrinkLabel)

  const handleFocus = () => setFocused(true)
  const handleBlur = () => setFocused(false)

  const [popupOpen, setPopupOpen] = useState(false)

  const {
    id,
    name,
    value,
    displayFormat,
    showTime,
    use12Hours,
    disabled,
    style,
    className,
    size,
    width,
    label,
    required,
    shrinkLabel
  } = props

  const dayjsValue = React.useMemo(
    () => (props.value ? dayjs(props.value) : null),
    [props.value]
  )

  // convert date-fns format string to dayjs format string for AntD
  const antdDisplayFormat = convertDateFnsToDayjsFormat(displayFormat)

  return (
    <>
      {popupOpen && (
        <div
          className='modal-picker-backdrop'
          onClick={() => setPopupOpen(false)}
        />
      )}
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: '#223d79'
          }
        }}
      >
        <div className={className || ''} style={style}>
          <FormControl
            fullWidth
            variant='outlined'
            size='small'
            style={{
              marginTop: 0,
              maxWidth: '350px',
              width: width ?? style?.width ?? '100%'
            }}
          >
            <InputLabel
              required={required}
              id={`${id}-label`}
              shrink={shrinkLabel || !!value || focused}
              className={classes.customPickerLabel}
              style={{
                top: shrinkLabel || !!value || focused ? 0 : '50%',
                transform:
                  shrinkLabel || !!value || focused
                    ? 'translateY(-50%) scale(0.75)'
                    : 'translateY(-50%)',
                background:
                  shrinkLabel || !!value || focused
                    ? 'linear-gradient(to bottom, transparent 50%, #fff 50%)'
                    : 'transparent'
              }}
            >
              {label}
            </InputLabel>

            <DatePicker
              id={id}
              name={name}
              value={dayjsValue}
              defaultPickerValue={dayjs()}
              format={antdDisplayFormat}
              showTime={
                showTime
                  ? {
                      showSecond: false,
                      use12Hours: use12Hours
                    }
                  : false
              }
              open={popupOpen}
              onOpenChange={setPopupOpen}
              placeholder=''
              popupClassName='modal-centered-popup'
              getPopupContainer={() => document.body}
              onChange={(date) => {
                const newDate = date ? date.toDate() : null
                if (props.onChange) props.onChange(newDate)
                else if (props.setValue) props.setValue(newDate)
              }}
              onFocus={shrinkLabel ? undefined : handleFocus}
              onBlur={handleBlur}
              disabled={disabled}
              size={size ?? 'small'}
              style={{
                borderRadius: 4,
                boxSizing: 'border-box',
                width: '100%'
              }}
              inputReadOnly={props.inputReadOnly}
            />
          </FormControl>
        </div>
      </ConfigProvider>
    </>
  )
}

export default DateTimePicker
